import React, { useContext, useEffect, useState } from 'react';
import { Helmet as MetaTags } from 'react-helmet';
import {
  navigate,
  RouteComponentProps,
  useLocation,
  WindowLocation,
} from '@gatsbyjs/reach-router';
import { FORM_ERROR } from 'final-form';
import styled from 'styled-components';
import { Auth } from '~/amplify';
import {
  AUTH_PATHS,
  PHONE_NUMBER_ATTRIBUTE_KEY,
} from '~/components/page/Auth/constants';
import sendPhoneVerificationCode from '~/amplify/utils/sendPhoneVerificationCode';
import { AuthContext } from '~/Root/Auth';
import { Body, Heading1 } from '~/components/atom/Typography';
import { Form, Field } from '~/components/organism/Forms';
import OTPInput from '~/components/molecule/OTPInput';
import PhoneVerificationField from '~/components/molecule/PhoneVerificationField';
import JustificationContainer from '~/components/atom/JustificationContainer';
import Button from '~/components/atom/Button';
import AuthFormWrapper from '../components/AuthFormWrapper';
import createPageTitle from '~/util/createPageTitle';
import { reporter } from '~/hooks/useErrorReporter';
import useAddToast from '~/hooks/useAddToast';
import formatToastMessage from '~/util/formatToastMessage';
import PhoneValidation from '~/util/Validation/Phone';
import { validateOtpCode } from '~/util/Validation/OTP';
import confirmPhoneNumberAttribute from '~/amplify/utils/confirmPhoneNumberAttribute';
import { isExpiredOrNotAuthorizedSessionError } from '~/Errors';
import Catalog from '~/Catalog';

const text = {
  title: 'Backup instellen',
  step1: 'Voer je mobiele telefoonnummer in als backup.',
  step2: 'Voer de verificatiecode in die je per SMS hebt ontvangen.',
  confirmButton: 'Bevestigen',
  invalidPhone: Catalog.mfaInvalidDutchPhoneNumber,
  codeLengthError: Catalog.mfaTotpCodeLengthError,
  invalidCode: 'Ongeldige code. Probeer het opnieuw.',
  successMessage: 'Tweestapsverificatie is succesvol ingesteld.',
};

type WindowState = {
  from?: string;
};

const MFASetupPhone: React.FC<RouteComponentProps> = () => {
  const location = useLocation() as WindowLocation<WindowState>;
  const { ensureLogin, user } = useContext(AuthContext);
  const [smsSent, setSmsSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const addToast = useAddToast();

  useEffect(() => {
    if (!user) return;

    const loadMFAPreferences = async () => {
      try {
        const [preferences, attributes] = await Promise.all([
          Auth.fetchMFAPreference(),
          Auth.fetchUserAttributes(),
        ]);

        const phone = attributes[PHONE_NUMBER_ATTRIBUTE_KEY] ?? null;
        const phoneVerified = attributes.phone_number_verified === 'true';

        const isSmsEnabled = preferences.enabled?.includes('SMS') ?? false;
        if (isSmsEnabled && phone && phoneVerified) {
          void navigate('/-/', { replace: true });
          return;
        }
      } catch (err) {
        reporter.captureMessage(
          `MFASetupPhone: Failed to load MFAPreferences: ${err}`,
          'error',
        );
      }
    };

    void loadMFAPreferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const onSendCode = async (phone: string) => {
    const normalizedPhone = PhoneValidation.normalizeDutchPhoneNumber(phone);

    if (!normalizedPhone) {
      setPhoneError(text.invalidPhone);
      return false;
    }

    setSendingCode(true);
    setPhoneError(null);

    try {
      const result = await Auth.updateUserAttribute({
        userAttribute: {
          attributeKey: PHONE_NUMBER_ATTRIBUTE_KEY,
          value: normalizedPhone,
        },
      });

      await sendPhoneVerificationCode(result);

      setSmsSent(true);
      return true;
    } catch (err) {
      if (isExpiredOrNotAuthorizedSessionError(err)) {
        void navigate(AUTH_PATHS.LOGIN, {
          replace: true,
          state: { message: Catalog.sessionExpired },
        });
        return;
      }
      reporter.captureMessage(
        `MFASetupPhone: Failed to update phone attribute: ${err}`,
        'error',
      );
      setPhoneError(text.invalidPhone);
      return false;
    } finally {
      setSendingCode(false);
    }
  };

  const onSubmit = async ({
    code,
  }: {
    code: string;
  }): Promise<{ [key: string]: string } | void> => {
    if (!smsSent) return;

    try {
      await confirmPhoneNumberAttribute(code);
      await Auth.updateMFAPreference({ sms: 'ENABLED' });

      addToast([formatToastMessage(text.successMessage, 'success')]);
      ensureLogin();
      void navigate(location.state?.from ?? '/-/', { replace: true });
    } catch (err) {
      reporter.captureMessage(
        `MFASetupPhone: Failed to confirm phone attribute: ${err}`,
        'error',
      );
      return { [FORM_ERROR]: text.invalidCode };
    }
  };

  const validate = ({ code }: { code: string }) => {
    if (!validateOtpCode({ code })) return { code: text.codeLengthError };
    return {};
  };

  if (!user) {
    return (
      <>
        <MetaTags>
          <title>{createPageTitle(text.title)}</title>
          <meta name="description" />
        </MetaTags>
        <JustificationContainer
          width="100%"
          align="center"
          justification="center"
        >
          <Body color={{ group: 'white' }} size="base" margin={[null]}>
            {Catalog.sessionExpired}
          </Body>
        </JustificationContainer>
      </>
    );
  }

  return (
    <>
      <MetaTags>
        <title>{createPageTitle(text.title)}</title>
      </MetaTags>
      <AuthFormWrapper>
        <Heading1
          lineHeight="base"
          padding={[null, null, 'xxxl', 'l']}
          color={{ group: 'accent' }}
          size="xxxl"
          skewed
          margin={[null]}
        >
          {text.title}
        </Heading1>

        <Form
          initialValues={{ code: '' }}
          onSubmit={onSubmit}
          validate={validate}
        >
          {({ handleSubmit, submitting, submitError: formError, form }) => (
            <form onSubmit={handleSubmit}>
              <JustificationContainer
                direction="column"
                gap="xl"
                width="100%"
                align="center"
                padding={['l']}
              >
                <Body color={{ group: 'white' }} size="base" margin={[null]}>
                  {text.step1}
                </Body>

                <PhoneVerificationField
                  onSendCode={onSendCode}
                  phoneError={phoneError}
                  submitting={submitting}
                  sendingCode={sendingCode}
                  smsSent={smsSent}
                  labelColor={{ group: 'white' }}
                />

                {smsSent && (
                  <CodeContainer
                    align="center"
                    direction="column"
                    gap="l"
                    width="100%"
                  >
                    <Body
                      color={{ group: 'white' }}
                      size="base"
                      margin={[null]}
                      dataTestId="mfa-setup-phone-code-instruction"
                    >
                      {text.step2}
                    </Body>

                    {formError && (
                      <Body
                        margin={[null]}
                        color={{ group: 'danger', variant: 'light' }}
                        size="base"
                      >
                        {formError}
                      </Body>
                    )}

                    <Field name="code">
                      {({ input }) => (
                        <OTPInput
                          length={6}
                          value={input.value ?? ''}
                          onChange={input.onChange}
                          onBlur={input.onBlur}
                          disabled={submitting}
                          autoFocus
                          onComplete={() => {
                            void form.submit();
                          }}
                        />
                      )}
                    </Field>
                  </CodeContainer>
                )}

                <JustificationContainer justification="center" width="100%">
                  {smsSent && (
                    <Button
                      appearance="primary"
                      size="medium"
                      type="submit"
                      disabled={submitting}
                      loading={submitting}
                      label={text.confirmButton}
                    />
                  )}
                </JustificationContainer>
              </JustificationContainer>
            </form>
          )}
        </Form>
      </AuthFormWrapper>
    </>
  );
};

const CodeContainer = styled(JustificationContainer)``;

export default MFASetupPhone;
